/**
 * **Lieferverträge eines Gebäudes** — Strom, Gas, Fernwärme, Fernkälte (`EnergySupplyContract`,
 * tri-server docs/metering.md).
 *
 * - Ein Vertrag gehört zu Hauptzählern; ihre Unterzähler erben ihn. Ein Zähler hat höchstens einen Vertrag.
 * - Der Emissionsfaktor des Versorgers (Stromkennzeichnung § 42 EnWG) ergibt die marktbasierten CO₂-Werte im
 *   Dashboard „Zählerdaten“; ohne ihn rechnet das Dashboard standortbasiert und sagt das.
 * - Verträge aus dem Datenraum (Dokumentrolle „Energieliefervertrag“) erscheinen hier mit Hinweis.
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FileText, Gauge, Plus, Save, Trash2 } from "lucide-react";
import { useModalContext } from "../../components/ModalProvider";
import { ENERGY_CARRIERS, EnergyCarrier, Medium, MeterNode, SupplyContract, useMeteringAPI } from "../../MeteringAPI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

/** Welche Zähler zu welchem Energieträger passen. */
const MEDIUM_OF: Record<EnergyCarrier, Medium | null> = {
  electricity: "electricity",
  naturalGas: "gas",
  biomethane: "gas",
  lpg: "gas",
  districtHeating: "heat",
  districtCooling: "cold",
  heatingOil: null,
  woodPellets: null,
  other: null,
};

const TEXT_FIELDS = ["supplierName", "contractNumber", "productName", "marketLocationIds", "emissionFactorSource"] as const;
const NUMBER_FIELDS = ["annualQuantity", "energyPrice", "basePrice", "emissionFactorMarket", "emissionFactorYear", "renewableShare", "calorificValue", "gasStateFactor"] as const;
const GAS: EnergyCarrier[] = ["naturalGas", "biomethane", "lpg"];
const UNITS: Record<(typeof NUMBER_FIELDS)[number], string> = {
  annualQuantity: "kWh/a",
  energyPrice: "ct/kWh",
  basePrice: "EUR/Jahr",
  calorificValue: "kWh/m³",
  gasStateFactor: "",
  emissionFactorMarket: "g CO₂e/kWh",
  emissionFactorYear: "",
  renewableShare: "%",
};

type Draft = { _id?: string; energyCarrier: string; guaranteesOfOrigin: boolean; meters: string[]; fields: Record<string, string> };

const day = (iso: string | null) => (iso ? iso.slice(0, 10) : "");
const toDraft = (c?: SupplyContract): Draft => ({
  ...(c ? { _id: c._id } : {}),
  fields: {
    ...Object.fromEntries([...TEXT_FIELDS, ...NUMBER_FIELDS].map(f => [f, c?.[f] == null ? "" : String(c[f])])),
    startDate: day(c?.startDate ?? null),
    endDate: day(c?.endDate ?? null),
  },
  energyCarrier: c?.energyCarrier ?? "electricity",
  guaranteesOfOrigin: !!c?.guaranteesOfOrigin,
  meters: c?.meters.map(m => m._id) ?? [],
});

export default function SupplyContractsPanel({ building }: { building: any }) {
  const { t } = useTranslation();
  const api = useMeteringAPI();
  const { pushError } = useModalContext() as any;
  const buildingId = String(building?._id ?? "");
  const [contracts, setContracts] = useState<SupplyContract[] | null>(null);
  const [roots, setRoots] = useState<MeterNode[]>([]);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [list, tree] = await Promise.all([api.supplyContracts(buildingId), api.meters(buildingId)]);
      setContracts(list);
      setRoots(Object.values(tree.media).flatMap(r => r ?? []));
    } catch (error: any) {
      pushError(error);
    }
  };
  useEffect(() => {
    if (buildingId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingId]);

  /** Hauptzähler, die zum Energieträger passen; bei Heizöl, Pellets usw. alle. */
  const candidates = useMemo(() => {
    const medium = draft ? MEDIUM_OF[draft.energyCarrier as EnergyCarrier] : null;
    return roots.filter(m => !medium || m.medium === medium);
  }, [roots, draft]);
  const contractName = (id: string | null) => {
    const c = contracts?.find(x => x._id === id);
    return c ? c.supplierName || c.productName || c.contractNumber || t("supplyContracts.unnamed") : null;
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const { _id, meters, fields, energyCarrier, guaranteesOfOrigin } = draft;
      const saved = await api.saveSupplyContract(buildingId, { ...fields, energyCarrier, guaranteesOfOrigin }, _id);
      const before = new Set(contracts?.find(c => c._id === _id)?.meters.map(m => m._id) ?? []);
      const after = new Set(meters);
      await Promise.all([
        ...meters.filter(m => !before.has(m)).map(m => api.updateMeter(m, { supplyContract: saved._id })),
        ...Array.from(before).filter(m => !after.has(m)).map(m => api.updateMeter(m, { supplyContract: null })),
      ]);
      await load();
      setDraft({ ...draft, _id: saved._id });
    } catch (error: any) {
      pushError(error);
    } finally {
      setSaving(false);
    }
  };
  const remove = async () => {
    if (!draft?._id) return;
    try {
      await api.deleteSupplyContract(buildingId, draft._id);
      setDraft(null);
      await load();
    } catch (error: any) {
      pushError(error);
    }
  };

  if (!contracts) return <Skeleton className="h-64 w-full" />;
  const field = (f: string, type = "text", unit = "") => (
    <label key={f} className="flex min-w-0 flex-col gap-1 text-sm">
      <span className="truncate">
        {t(`supplyContracts.field.${f}`)}
        {unit ? <span className="text-muted-foreground"> ({unit})</span> : null}
      </span>
      <Input type={type} value={draft?.fields[f] ?? ""} onChange={e => draft && setDraft({ ...draft, fields: { ...draft.fields, [f]: e.target.value } })} />
    </label>
  );

  return (
    <div className="grid min-w-0 gap-3 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]" data-testid="supply-contracts">
      <div className="flex min-w-0 flex-col gap-2">
        <Button size="sm" className="self-start" onClick={() => setDraft(toDraft())}>
          <Plus className="mr-1 h-4 w-4" /> {t("supplyContracts.add")}
        </Button>
        {contracts.length ? (
          contracts.map(c => (
            <button
              key={c._id}
              type="button"
              onClick={() => setDraft(toDraft(c))}
              className={`flex min-w-0 flex-col gap-1 rounded-md border p-3 text-left text-sm ${draft?._id === c._id ? "border-primary bg-primary/5" : "hover:bg-muted"}`}
            >
              <span className="flex items-center gap-2 font-semibold">
                <span className="min-w-0 truncate">{c.supplierName || c.productName || c.contractNumber || t("supplyContracts.unnamed")}</span>
                {c.fromDocument ? <FileText aria-label={t("supplyContracts.fromDocument")} className="h-4 w-4 shrink-0 text-muted-foreground" /> : null}
              </span>
              <span className="text-xs text-muted-foreground">
                {c.energyCarrier ? t(`supplyContracts.carrier.${c.energyCarrier}`) : "—"}
                {c.productName && c.supplierName ? ` · ${c.productName}` : ""}
                {c.startDate ? ` · ${t("supplyContracts.since", { date: new Date(c.startDate).toLocaleDateString("de-DE") })}` : ""}
              </span>
              <span className="flex flex-wrap gap-1">
                <Badge variant={c.emissionFactorMarket != null ? "secondary" : "outline"} className="text-[10px] tabular-nums">
                  {c.emissionFactorMarket != null ? `${c.emissionFactorMarket} g CO₂e/kWh${c.emissionFactorYear ? ` (${c.emissionFactorYear})` : ""}` : t("supplyContracts.noFactor")}
                </Badge>
                <Badge variant="outline" className="text-[10px]">
                  {t("supplyContracts.meterCount", { count: c.meters.length })}
                </Badge>
              </span>
            </button>
          ))
        ) : (
          <p className="text-sm text-muted-foreground">{t("supplyContracts.empty")}</p>
        )}
      </div>

      {draft ? (
        <div className="flex min-w-0 flex-col gap-3 rounded-md border p-3">
          <div className="grid gap-3 sm:grid-cols-2">
            {field("supplierName")}
            <label className="flex min-w-0 flex-col gap-1 text-sm">
              {t("supplyContracts.field.energyCarrier")}
              <Select value={draft.energyCarrier} onValueChange={v => setDraft({ ...draft, energyCarrier: v })}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ENERGY_CARRIERS.map(c => (
                    <SelectItem key={c} value={c}>
                      {t(`supplyContracts.carrier.${c}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
            {field("productName")}
            {field("contractNumber")}
            {field("startDate", "date")}
            {field("endDate", "date")}
            {field("marketLocationIds")}
            {field("annualQuantity", "number", UNITS.annualQuantity)}
            {field("energyPrice", "number", UNITS.energyPrice)}
            {field("basePrice", "number", UNITS.basePrice)}
            {field("renewableShare", "number", UNITS.renewableShare)}
            {GAS.includes(draft.energyCarrier as EnergyCarrier) ? (
              <>
                {field("calorificValue", "number", UNITS.calorificValue)}
                {field("gasStateFactor", "number")}
                <p className="text-xs text-muted-foreground sm:col-span-2">{t("supplyContracts.gasHint")}</p>
              </>
            ) : null}
          </div>

          <div className="min-w-0 rounded-md border p-2">
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("supplyContracts.emissions")}</div>
            <div className="grid gap-3 sm:grid-cols-3">
              {field("emissionFactorMarket", "number", UNITS.emissionFactorMarket)}
              {field("emissionFactorYear", "number")}
              {field("emissionFactorSource")}
            </div>
            <label className="mt-2 flex items-center gap-2 text-sm">
              <Checkbox checked={draft.guaranteesOfOrigin} onCheckedChange={(v: boolean | "indeterminate") => setDraft({ ...draft, guaranteesOfOrigin: v === true })} />
              {t("supplyContracts.field.guaranteesOfOrigin")}
            </label>
            <p className="mt-1 text-xs text-muted-foreground">{t("supplyContracts.emissionsHint")}</p>
          </div>

          <div className="min-w-0 rounded-md border p-2">
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("supplyContracts.meters")}</div>
            {candidates.length ? (
              <div className="grid gap-1 sm:grid-cols-2">
                {candidates.map(m => {
                  const other = m.supplyContract && m.supplyContract !== draft._id ? contractName(m.supplyContract) : null;
                  return (
                    <label key={m._id} className="flex min-w-0 items-center gap-2 text-sm">
                      <Checkbox
                        checked={draft.meters.includes(m._id)}
                        onCheckedChange={(v: boolean | "indeterminate") =>
                          setDraft({ ...draft, meters: v === true ? Array.from(new Set([...draft.meters, m._id])) : draft.meters.filter(x => x !== m._id) })
                        }
                      />
                      <Gauge aria-hidden className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="min-w-0 truncate">{m.name}</span>
                      {other ? <span className="shrink-0 text-xs text-amber-700">{t("supplyContracts.replaces", { name: other })}</span> : null}
                    </label>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">{t("supplyContracts.noMeters")}</p>
            )}
            <p className="mt-1 text-xs text-muted-foreground">{t("supplyContracts.metersHint")}</p>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={save} disabled={saving}>
              <Save className="mr-1 h-4 w-4" /> {t("supplyContracts.save")}
            </Button>
            {draft._id ? (
              <Button size="sm" variant="outline" onClick={remove}>
                <Trash2 className="mr-1 h-4 w-4" /> {t("supplyContracts.delete")}
              </Button>
            ) : null}
            <Button size="sm" variant="ghost" onClick={() => setDraft(null)}>
              {t("supplyContracts.close")}
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">{t("supplyContracts.pick")}</p>
      )}
    </div>
  );
}
